import { useEffect, useMemo, useState } from "react";
import { useSession } from "../lib/session";
import {
  apiReportInsights,
  apiReportOrderSummary,
  apiReportSpendingsBy,
} from "../lib/api";

/**
 * Dashboard — the real first-page-after-login on buyer.zeemart.co (observed
 * via /contract observe-original; see docs/observed/screen-flow.md). All
 * rendering is real components against the observed response shapes — never
 * raw JSON dumps. The catalog/json-render path is reserved for the agent
 * chat panel; static pages render directly.
 */

// --- response types (observed) -------------------------------------------

type OrderSummary = {
  totalSpendingCurrWeek?: number;
  totalSpendingLastWeek?: number;
  totalSpendingCurrDay?: number;
  totalSpendingCurrMonth?: number;
  totalSpendingLastMonth?: number;
  totalSpendingQuarterly?: number;
  todayPendingDeliveries?: number;
  currWeekOrderCount?: number;
  lastWeekOrderCount?: number;
  currMonthOrderCount?: number;
  lastMonthOrderCount?: number;
  goalPercentage?: number;
  isGoalActive?: boolean;
};

type SupplierRow = {
  supplier?: {
    supplierId?: string;
    supplierName?: string;
    alias?: string;
    logoURL?: string;
  };
  totalSpendingAmount?: number;
  subTotalSpendingAmount?: number;
  pastTotalSpendingAmount?: number;
  spendingPercentage?: number;
};

type CategoryRow = {
  category?: string;
  totalSpendingOnCategoryAmount?: number;
  pastTotalSpendingOnCategoryAmount?: number;
  spendingPercentage?: number;
};

type Insight = {
  uuid?: string;
  title?: string;
  shortDescription?: string;
  longDescription?: string;
  type?: string;
};

type InsightsEnv = { insights?: Insight[] };

// --- data hook -----------------------------------------------------------

type Loaded<T> = { data: T | null; err: string | null };

function useReport<T>(enabled: boolean, key: unknown[], fn: () => Promise<unknown>): Loaded<T> {
  const [s, setS] = useState<Loaded<T>>({ data: null, err: null });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setS({ data: null, err: null });
    fn()
      .then((d) => {
        if (cancelled) return;
        // Zeemart wraps responses as { status, message, data, ... }. Unwrap.
        const unwrapped =
          d && typeof d === "object" && "data" in (d as Record<string, unknown>)
            ? (d as { data: unknown }).data
            : d;
        setS({ data: unwrapped as T, err: null });
      })
      .catch((e: unknown) => !cancelled && setS({ data: null, err: e instanceof Error ? e.message : String(e) }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...key]);
  return s;
}

// --- formatting ----------------------------------------------------------

function money(n: number | undefined | null, currency = "S$"): string {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  return `${currency} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function pct(n: number | undefined | null): string {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  return `${n.toFixed(1)}%`;
}
function deltaPercent(curr: number | undefined, prev: number | undefined): { value: string; tone: "up" | "down" | "flat" } {
  if (typeof curr !== "number" || typeof prev !== "number" || prev === 0) {
    return { value: "—", tone: "flat" };
  }
  const d = ((curr - prev) / prev) * 100;
  const tone = Math.abs(d) < 0.5 ? "flat" : d > 0 ? "up" : "down";
  const sign = d > 0 ? "+" : "";
  return { value: `${sign}${d.toFixed(1)}% vs prior period`, tone };
}

// --- visual primitives ---------------------------------------------------

function Kpi({ label, value, sub, tone = "flat" }: { label: string; value: string; sub?: string; tone?: "up" | "down" | "flat" }) {
  const subClass = tone === "up" ? "text-nomni-leaf" : tone === "down" ? "text-danger" : "text-ink-mute";
  return (
    <div className="rounded-[var(--radius-card)] bg-panel ring-1 ring-rule p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-ink-mute">{label}</div>
      <div className="text-3xl tabular-nums tracking-tight text-ink mt-1">{value}</div>
      {sub ? <div className={`text-xs mt-1 ${subClass}`}>{sub}</div> : null}
    </div>
  );
}

function Bar({ percent, tone = "nomni" }: { percent: number; tone?: "nomni" | "amber" }) {
  const pct = Math.max(0, Math.min(100, percent));
  const fill = tone === "amber" ? "bg-amber" : "bg-nomni";
  return (
    <div className="h-1.5 rounded-full bg-rule overflow-hidden">
      <div className={`h-full ${fill}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function RankedRow({
  primary,
  secondary,
  amount,
  percent,
  initials,
  tone,
}: {
  primary: string;
  secondary?: string;
  amount: string;
  percent?: number;
  initials?: string;
  tone?: "nomni" | "amber";
}) {
  return (
    <div className="rounded-[var(--radius-card)] bg-panel ring-1 ring-rule px-4 py-3">
      <div className="flex items-center gap-3 mb-2">
        {initials ? (
          <div className="size-9 rounded-lg bg-nomni/15 text-nomni-deep grid place-items-center font-medium text-sm">
            {initials.slice(0, 2).toUpperCase()}
          </div>
        ) : null}
        <div className="flex-1 min-w-0">
          <div className="text-ink font-medium truncate">{primary}</div>
          {secondary ? <div className="text-xs text-ink-mute truncate">{secondary}</div> : null}
        </div>
        <div className="text-ink tabular-nums font-medium">{amount}</div>
      </div>
      {typeof percent === "number" ? (
        <div className="flex items-center gap-2">
          <Bar percent={percent} tone={tone} />
          <div className="text-[11px] tabular-nums text-ink-mute w-12 text-right">{pct(percent)}</div>
        </div>
      ) : null}
    </div>
  );
}

function InsightCard({ insight }: { insight: Insight }) {
  return (
    <div className="rounded-[var(--radius-card)] bg-panel ring-1 ring-rule p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-nomni-leaf mb-1">
        {(insight.type ?? "INSIGHT").replace(/_/g, " ").toLowerCase()}
      </div>
      <div className="text-ink font-medium">{insight.title ?? "Insight"}</div>
      {insight.shortDescription ? (
        <p className="text-sm text-ink-2 mt-2 leading-relaxed text-pretty">{insight.shortDescription}</p>
      ) : null}
    </div>
  );
}

function SectionHeader({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] uppercase tracking-[0.18em] text-ink-mute">{eyebrow}</div>
      <h2 className="text-xl tracking-tight text-ink mt-0.5">{title}</h2>
    </div>
  );
}

// --- page ----------------------------------------------------------------

export function Dashboard() {
  const { session } = useSession();

  const allOutletIds = useMemo(
    () => (session.outlets ?? []).map((o) => o.outletId),
    [session.outlets],
  );
  const ready = allOutletIds.length > 0;

  const now = useMemo(() => Math.floor(Date.now() / 1000), []);
  const SEVEN_DAYS = 7 * 24 * 3600;
  const slice = useMemo(
    () => ({
      startDate: now - SEVEN_DAYS,
      endDate: now,
      refStartDate: now - 2 * SEVEN_DAYS,
      refEndDate: now - SEVEN_DAYS,
    }),
    [now],
  );

  const orderSummary = useReport<OrderSummary>(ready, [allOutletIds.join(",")], () =>
    apiReportOrderSummary(allOutletIds),
  );
  const insights = useReport<InsightsEnv>(ready, [allOutletIds.join(",")], () =>
    apiReportInsights(allOutletIds, "PAST_7_DAYS"),
  );
  const bySupplier = useReport<SupplierRow[]>(ready, [allOutletIds.join(","), slice], () =>
    apiReportSpendingsBy(allOutletIds, slice.startDate, slice.endDate, slice.refStartDate, slice.refEndDate, "supplier"),
  );
  const byCategory = useReport<CategoryRow[]>(ready, [allOutletIds.join(","), slice], () =>
    apiReportSpendingsBy(allOutletIds, slice.startDate, slice.endDate, slice.refStartDate, slice.refEndDate, "category"),
  );

  const sum = orderSummary.data ?? {};
  const insightList = insights.data?.insights ?? [];
  const suppliers = (bySupplier.data ?? []).slice(0, 5);
  const categories = (byCategory.data ?? []).slice(0, 5);

  const weekDelta = deltaPercent(sum.totalSpendingCurrWeek, sum.totalSpendingLastWeek);
  const monthDelta = deltaPercent(sum.totalSpendingCurrMonth, sum.totalSpendingLastMonth);
  const wkOrdersDelta = deltaPercent(sum.currWeekOrderCount, sum.lastWeekOrderCount);

  return (
    <div className="px-8 py-6 max-w-6xl mx-auto rise space-y-10">
      <header>
        <h1 className="text-3xl font-medium tracking-tight text-ink">
          Welcome back{session.user?.firstName ? `, ${session.user.firstName}` : ""}
        </h1>
        <p className="text-ink-mute mt-1">
          {ready
            ? `Last 7 days across ${allOutletIds.length} outlet${allOutletIds.length === 1 ? "" : "s"}`
            : "Pick an outlet from the topbar to begin."}
        </p>
      </header>

      {!ready ? null : orderSummary.err ? (
        <div className="rounded-[var(--radius-card)] bg-danger-soft/40 ring-1 ring-danger/40 p-5 text-sm text-ink-2">
          Could not load your summary. {orderSummary.err}
        </div>
      ) : (
        <section>
          <SectionHeader eyebrow="At a glance" title="Spend & orders" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi
              label="Spend · this week"
              value={money(sum.totalSpendingCurrWeek)}
              sub={weekDelta.value}
              tone={weekDelta.tone}
            />
            <Kpi
              label="Today"
              value={money(sum.totalSpendingCurrDay)}
              sub={
                typeof sum.todayPendingDeliveries === "number"
                  ? `${sum.todayPendingDeliveries} pending deliver${sum.todayPendingDeliveries === 1 ? "y" : "ies"}`
                  : undefined
              }
            />
            <Kpi
              label="Spend · this month"
              value={money(sum.totalSpendingCurrMonth)}
              sub={monthDelta.value}
              tone={monthDelta.tone}
            />
            <Kpi
              label="Orders · this week"
              value={typeof sum.currWeekOrderCount === "number" ? String(sum.currWeekOrderCount) : "—"}
              sub={wkOrdersDelta.value}
              tone={wkOrdersDelta.tone}
            />
          </div>
        </section>
      )}

      <section>
        <SectionHeader eyebrow="Top spend · last 7 days" title="Suppliers" />
        {!ready ? null : bySupplier.err ? (
          <div className="text-sm text-ink-mute">Could not load supplier spend.</div>
        ) : bySupplier.data === null ? (
          <SkeletonRows n={4} />
        ) : suppliers.length === 0 ? (
          <EmptyHint title="No supplier spend this week" body="Once orders flow, top suppliers ranked by spend show here." />
        ) : (
          <SupplierList rows={suppliers} />
        )}
      </section>

      <section>
        <SectionHeader eyebrow="Top spend · last 7 days" title="Categories" />
        {!ready ? null : byCategory.err ? (
          <div className="text-sm text-ink-mute">Could not load category spend.</div>
        ) : byCategory.data === null ? (
          <SkeletonRows n={4} />
        ) : categories.length === 0 ? (
          <EmptyHint title="No category spend this week" body="Once orders flow, your most-spent categories show here." />
        ) : (
          <CategoryList rows={categories} />
        )}
      </section>

      <section>
        <SectionHeader eyebrow="From Zeemart" title="Insights" />
        {!ready ? null : insights.err ? (
          <div className="text-sm text-ink-mute">Could not load insights.</div>
        ) : insights.data === null ? (
          <SkeletonRows n={3} />
        ) : insightList.length === 0 ? (
          <EmptyHint title="No insights for this window" body="Insights surface when your order history gives Zeemart enough signal." />
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {insightList.slice(0, 6).map((i, idx) => (
              <InsightCard key={i.uuid ?? idx} insight={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// --- helpers (private) ---------------------------------------------------

function SkeletonRows({ n }: { n: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="h-14 rounded-[var(--radius-card)] shimmer bg-rule/60" />
      ))}
    </div>
  );
}

function EmptyHint({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-[var(--radius-card)] bg-cream/50 ring-1 ring-rule px-5 py-6 text-center">
      <div className="text-ink font-medium">{title}</div>
      <p className="text-sm text-ink-mute mt-1 text-balance">{body}</p>
    </div>
  );
}

function SupplierList({ rows }: { rows: SupplierRow[] }) {
  const max = Math.max(1, ...rows.map((r) => r.totalSpendingAmount ?? 0));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const name = r.supplier?.supplierName ?? r.supplier?.alias ?? "(unnamed supplier)";
        const amt = r.totalSpendingAmount ?? r.subTotalSpendingAmount ?? 0;
        return (
          <RankedRow
            key={r.supplier?.supplierId ?? i}
            primary={name}
            secondary={r.supplier?.supplierId}
            amount={money(amt)}
            percent={(amt / max) * 100}
            initials={name}
            tone="nomni"
          />
        );
      })}
    </div>
  );
}

function CategoryList({ rows }: { rows: CategoryRow[] }) {
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <RankedRow
          key={r.category ?? i}
          primary={r.category ?? "(uncategorized)"}
          amount={money(r.totalSpendingOnCategoryAmount)}
          percent={r.spendingPercentage ?? 0}
          tone="amber"
        />
      ))}
    </div>
  );
}
